import { useCallback, useEffect, useMemo, useState } from 'react';

import { applyBatchLotUsage } from '../utils/batchLotStatus';
import { batchShareForRow, settleBatchClaims, stagedClaimLotIds } from '../utils/batchLotUsage';
import { planBulkApply } from '../utils/bulkApplyPlan';

/**
 * POM-6536 — the review batch weighed lot by lot, and the claims staged against it.
 *
 * A claim is staged from Change NDC & lot when a row keeps its own lot and the batch leaves too
 * little of it: Apply there is the operator's confirmation. It is held apart from `changes`
 * because it commits no lot and rebuilds no label; core grants the permission to every row on
 * the lot instead (POM-6535's claim-only path).
 *
 * `claims` maps requestLineItemId -> the inventoryLotId it claims.
 */
const useBatchLotClaims = ({
  open,
  payload,
  classified,
  changes,
  isManualLotEnabled,
  todayStr,
}) => {
  const [claims, setClaims] = useState({});

  useEffect(() => {
    if (open) setClaims({});
  }, [open]);

  const items = payload?.items;
  const availability = payload?.availability;

  const { usage, activeClaims } = useMemo(
    () => settleBatchClaims({ items, availability, changes, claims }),
    [items, availability, changes, claims]
  );

  const review = useMemo(
    () => applyBatchLotUsage({ classified, usage, claims: activeClaims, isManualLotEnabled }),
    [classified, usage, activeClaims, isManualLotEnabled]
  );

  const claimShortfallLotIds = useMemo(
    () => [...stagedClaimLotIds({ changes, claims: activeClaims })],
    [changes, activeClaims]
  );

  /** Stage a claim for this row, or drop it with a null lot id. */
  const setClaim = useCallback(
    (requestLineItemId, inventoryLotId) =>
      setClaims((current) => {
        const next = { ...current };
        if (inventoryLotId) next[requestLineItemId] = inventoryLotId;
        else delete next[requestLineItemId];

        return next;
      }),
    []
  );

  const clearClaims = useCallback(() => setClaims({}), []);

  const shareForRow = useCallback(
    (item) => (item ? batchShareForRow({ items, availability, changes, item }) : null),
    [items, availability, changes]
  );

  // POM-6537 — the rows one picked lot can also go to, weighed against this same batch.
  const recordableLots = payload?.recordableLots;
  const planBulkApplyFor = useCallback(
    ({ item, selectedNdc, selection, scope, untickedIds }) =>
      planBulkApply({
        classified,
        claims,
        items,
        availability,
        recordableLots,
        changes,
        todayStr,
        isManualLotEnabled,
        editedItem: item,
        selectedNdc,
        selection,
        scope,
        untickedIds,
      }),
    [classified, claims, items, availability, recordableLots, changes, todayStr, isManualLotEnabled]
  );

  return {
    review,
    // As staged, before settling: what Undo bulk change puts back.
    claims,
    claimShortfallLotIds,
    setClaim,
    clearClaims,
    shareForRow,
    planBulkApplyFor,
  };
};

export default useBatchLotClaims;
